import { Key } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AdminFormRow, AdminFormSection } from '@ui';
import { MainContactForm } from '@lp/features/admin/forms/MainContactForm';
import { TMDXFile } from '@/api/apis/files';
import { useInfiniteHospitalListQuery } from '@/queries/hospital';
import { getFullName, renderAutocompleteItem } from '@/utils/helpers';
import { ContactFormValues } from '@/types';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';

type HospitalContactFormProps = {
  isPrimary?: boolean;
  isReadOnly?: boolean;
};

export const HospitalContactForm = ({ isPrimary = false, isReadOnly = false }: HospitalContactFormProps) => {
  const { control, setValue } = useFormContext<ContactFormValues>();
  const [parentName, photoPath, firstName, lastName] = useWatch({
    control,
    name: ['parentName', 'photoPath', 'firstName', 'lastName'],
  });

  const {
    searchValue: locationSearchValue,
    list: locationList,
    isLoading,
    onSearchValue: onLocationSearch,
    setAutocompleteOpen,
    itemsScrollerRef,
    onItemSelectChange: onLocationSelect,
  } = useInfiniteDropdown({
    setValue: (value: Key | null) => {
      if (!value) {
        setValue('parentId', '', { shouldValidate: true });
        setValue('parentName', '', { shouldValidate: true });
        return;
      }
      const location = locationList.find((h) => h.value === value.toString());
      if (location) {
        setValue('parentId', location.value, { shouldValidate: true });
        setValue('parentName', location.name, { shouldValidate: true });
      }
    },
    listQuery: useInfiniteHospitalListQuery,
    defaultSortOrder: { orderBy: 'name', asc: true },
    initialName: parentName || '',
    clearOnSearch: false,
  });

  const onImageChange = (file: TMDXFile) => {
    setValue('photo', file);
  };

  const autocompleteTitle = 'Donor Hospital Name';

  return (
    <AdminFormSection
      title="Contact Details"
      withAvatar
      isPrimary={isPrimary}
      isReadOnly={isReadOnly}
      imageUrl={photoPath}
      onImageChange={onImageChange}
      name={getFullName(firstName, lastName)}
    >
      <AdminFormRow title={autocompleteTitle} isRequired>
        <Controller
          name="parentId"
          control={control}
          render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
            <Autocomplete
              allowsCustomValue={false}
              placeholder="Choose an option"
              aria-label={autocompleteTitle}
              inputValue={locationSearchValue || parentName}
              scrollRef={itemsScrollerRef}
              items={locationList}
              isLoading={isLoading}
              onInputChange={(text) => {
                onLocationSearch(text);
                if (!text) {
                  onChange('');
                  onLocationSelect(null);
                }
              }}
              isInvalid={invalid}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              onOpenChange={setAutocompleteOpen}
              selectedKey={value || null}
              onSelectionChange={(key) => {
                onChange(key);
                onLocationSelect(key);
              }}
              isClearable
              {...field}
            >
              {renderAutocompleteItem}
            </Autocomplete>
          )}
        />
      </AdminFormRow>
      <MainContactForm isReadOnly={isReadOnly} />
    </AdminFormSection>
  );
};
